import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { Button, Grid, Icon, Input } from 'semantic-ui-react';
import PopupWrapper from '../../../common/components/PopupWrapper/PopupWrapper';
import CustomMenu from '../../../common/components/CustomMenu/CustomMenu';
import InformationModal from '../../../common/components/CustomModal/InformationModal/InformationModal';
import tracker from '../../../common/utils/tracking/tracker';
import * as trackerConstants from '../../../common/utils/tracking/tracking.consts';
import ValidationErrorMessage from '../../../common/components/ValidationErrorMessage/ValidationErrorMessage';
import withAuthorization from '../../../common/helpers/authorization';
import * as appConstants from '../../App/app.constants';
import * as constants from '../emails.constants';
import * as emailConstants from '../../Email/email.constants';

const EmailsCampaignToolbar = (props) => {
  const { campaignIndex, campaign, locationId, createEmail, handleOnBlur, updateCampaign,
    openToast, userAccessGroups, pinCampaign, hasFullPermission, focus, intl } = props;
  const { formatMessage } = intl;
  const [isCampaignMenuPopupOpen, setIsCampaignMenuPopupOpen] = useState(false);
  const [isArchiveInformationOpen, setIsArchiveInformationOpen] = useState(false);
  const [hasError, setHasError] = useState(false);

  const handleOnFocus = (e) => {
    if (e.target.setSelectionRange) {
      const len = e.target.value.length * 2;
      e.target.setSelectionRange(0, len);
    }
  };

  const handleCampaignMenuOpen = () => {
    setIsCampaignMenuPopupOpen(true);
  };

  const handleCampaignMenuClose = () => {
    setIsCampaignMenuPopupOpen(false);
  };

  const getCampaignContextMenu = () => {
    const isArchive = campaign.get(constants.CAMPAIGN_STATUS) === constants.CAMPAIGN_STATUS_ARCHIVE;

    return [
      {
        text: formatMessage({ id: isArchive ? 'business-center.campaign.menu.un-archive' : 'business-center.campaign.menu.archive' }),
        iconName: 'como-ic-archive',
        cb: () => {
          handleCampaignMenuClose();

          const hasActiveEmail = campaign.get(constants.EMAILS).some((email) => {
            const viewStatus = email.get(constants.CAMPAIGN_STATUS);
            return viewStatus === emailConstants.EMAIL_STATUS_ACTIVE;
          });

          if (hasActiveEmail) {
            return setIsArchiveInformationOpen(true);
          }

          tracker.onButtonClick(isArchive
            ? trackerConstants.BUTTON_TYPE_CAMPAIGN_UN_ARCHIVE
            : trackerConstants.BUTTON_TYPE_CAMPAIGN_ARCHIVE
          );
          return updateCampaign(campaign.set(constants.CAMPAIGN_STATUS,
            isArchive ? constants.CAMPAIGN_STATUS_ACTIVE : constants.CAMPAIGN_STATUS_ARCHIVE));
        }
      }
    ];
  };

  const handleCloseInformationModal = () => {
    setIsArchiveInformationOpen(false);
  };

  const handleSubmitCampaignName = (e) => {
    const newName = e.target.value;

    if (!newName) {
      setHasError(true);
      return;
    }

    setHasError(false);

    const nameChanged = campaign.get(constants.CAMPAIGN_NAME) !== newName;
    const newCampaign = campaign.set(constants.CAMPAIGN_NAME, newName);
    updateCampaign(newCampaign);
    handleOnBlur();

    if (nameChanged) {
      tracker.onEvent(trackerConstants.EVENT_TYPE_EMAILS_CAMPAIGN_NAME_CHANGED);
    }
  };

  const handleOnKeyDown = (e) => {
    if (e.keyCode === 13) {
      const nameChanged = campaign.get(constants.CAMPAIGN_NAME) !== e.target.value;
      if (nameChanged) {
        tracker.onEvent(trackerConstants.EVENT_TYPE_EMAILS_CAMPAIGN_NAME_CHANGED, {
          [trackerConstants.CAMPAIGN_NAME_CHANGED_ARGS_SOURCE]: trackerConstants.EVENT_SOURCE_ENTER_KEY
        });
      }
      e.target.blur();
    }
  };

  const handlePinCampaign = () => {
    pinCampaign(campaign);
    openToast(formatMessage({ id: 'business-center.group.pinned' }));
  };

  const handleUnpinCampaign = () => {
    const updatedCampaign = campaign.set(constants.CAMPAIGN_PINNED_DATE, null);
    updateCampaign(updatedCampaign);
    openToast(formatMessage({ id: 'business-center.group.unpinned' }));
  };

  const isAccessGroupFeatureEnable = hasFullPermission(appConstants.FEATURES_ACCESS_GROUPS);
  const disableFeaturesForAccessGroupUser = isAccessGroupFeatureEnable && userAccessGroups && userAccessGroups.size > 0;
  const campaignKey = campaign.get(constants.CAMPAIGN_ID);
  const isCampaignPinned = campaign.get(constants.CAMPAIGN_PINNED_DATE);

  return (
    <Grid
      columns="2"
      className="campaign-toolbar__container"
    >
      <InformationModal
        open={isArchiveInformationOpen}
        onConfirm={handleCloseInformationModal}
      >
        <p>{formatMessage({ id: 'business-center.emails_campaign.archive.information.content' })}</p>
      </InformationModal>
      <Grid.Column width={12}>
        <div className="campaign-title__container">
          <Input
            autoFocus={focus}
            type="text"
            defaultValue={campaign.get(constants.CAMPAIGN_NAME)}
            className="ui header campaign-title"
            onBlur={handleSubmitCampaignName}
            onKeyDown={handleOnKeyDown}
            onFocus={handleOnFocus}
            data-automation-id={`emails.campaign.${campaignIndex}.title`}
          />
          {
            hasError ?
              (<ValidationErrorMessage
                className="campaign-name-error"
                errorMessage={formatMessage({ id: 'campaign.name.error.empty' })}
                dataAutomationId={`emails.campaign.${campaignIndex}.error.message`}
              />)
              :
              null
          }
        </div>
      </Grid.Column>
      <Grid.Column width={4} className="ui right aligned column campaign-name">
        <div className={'tw-flex tw-justify-end tw-items-center'}>
          <i
            title={isCampaignPinned ? formatMessage({ id: 'business-center.campaign.pinned-title' }) : formatMessage({ id: 'business-center.campaign.pin-title' })}
            className={`como-svg-campaign-${isCampaignPinned ? 'pinned' : 'pin'} tw-cursor-pointer`}
            onClick={disableFeaturesForAccessGroupUser ? null : () => isCampaignPinned ? handleUnpinCampaign() : handlePinCampaign()}
            aria-hidden="true"
            style={disableFeaturesForAccessGroupUser ? { cursor: 'not-allowed', opacity: 0.5 } : {}}
          />
          <Button
            className="create-email-button"
            data-automation-id="emails-campaign-toolbar.add_email"
            onClick={() => tracker.wrapButtonAction(createEmail(locationId, campaignKey),
              trackerConstants.BUTTON_TYPE_CREATE_ONE_TIME_ACTIVITY)}
          >
            <Icon className="como-ic-plus" />
            <span>{formatMessage({ id: 'emails-page.add_email' })}</span>
          </Button>
          {!disableFeaturesForAccessGroupUser && (<PopupWrapper
            className="no-padding no-border"
            on="click"
            position="bottom right"
            open={isCampaignMenuPopupOpen}
            onOpen={handleCampaignMenuOpen}
            onClose={handleCampaignMenuClose}
            automationId={`emails.campaign.${campaignIndex}.contextual-menu.popup`}
            trigger={
              <Icon
                className="como-ic-more"
                data-automation-id={`emails.campaign.${campaignIndex}.contextual-menu`}
              />
            }
          >
            <CustomMenu
              AutomationIdPrefix={`emails.campaign.${campaignIndex}.contextual-menu`}
              data={getCampaignContextMenu()}
            />
          </PopupWrapper>)}
        </div>
      </Grid.Column>
    </Grid>
  );
};

EmailsCampaignToolbar.propTypes = {
  intl: PropTypes.object,
  campaignIndex: PropTypes.number,
  campaign: PropTypes.object,
  locationId: PropTypes.number,
  createEmail: PropTypes.func,
  handleOnBlur: PropTypes.func,
  updateCampaign: PropTypes.func,
  openToast: PropTypes.func,
  userAccessGroups: PropTypes.object,
  hasFullPermission: PropTypes.func,
  pinCampaign: PropTypes.func,
  focus: PropTypes.bool
};

export default withAuthorization(injectIntl(EmailsCampaignToolbar));
